import React, { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {Order, OrderWithActiveDelivery, DeliveryFormData, Address} from '../types';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import ConfirmModal from '../components/ConfirmModel';
import StatusBadge from '../components/StatusBadge';
import { useCustomers } from '../hooks/useCustomers';
import { fetchOrdersWithoutDeliveriesFromDb, fetchSingleOrderFromDb } from '../services/orderService';
import {
    fetchOrdersWithActiveDeliveriesFromDb,
    assignDriverToOrderInDb,
    unassignDriverFromOrderInDb,
} from '../services/deliveryService';
import { ORDER_STATUS, EXCLUDED_DISPATCH_STATUSES } from '../constants/orderStatuses';
import ActiveDeliveriesTable from "../components/ActiveDeliveriesTable.tsx";
import OrderTypeBadge from "../components/OrderTypeBadge.tsx";

const formatDate = (d?: string | null) => d ? new Date(d).toLocaleString() : '–';



const Dispatch: FC = () => {
    const navigate = useNavigate();
    const { fetchAddressesForCustomer } = useCustomers();

    const [ordersWithoutDeliveries,    setOrdersWithoutDeliveries]    = useState<Order[] | null>(null);
    const [pendingAcceptance,          setPendingAcceptance]          = useState<OrderWithActiveDelivery[] | null>(null);
    const [ordersWithActiveDeliveries, setOrdersWithActiveDeliveries] = useState<OrderWithActiveDelivery[] | null>(null);
    const [loading,                    setLoading]                    = useState(false);
    const [isUnassignModalOpen,        setIsUnassignModalOpen]        = useState(false);
    const [orderToUnassign,            setOrderToUnassign]            = useState<string | number | null>(null);
    const [isCreateDeliveryModalOpen,  setIsCreateDeliveryModalOpen]  = useState(false);
    const [selectedOrder,              setSelectedOrder]              = useState<Order | null>(null);
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [newDelivery,                setNewDelivery]                = useState<DeliveryFormData>({
        destination_id: '',
        driver_id:      '',
    });

    const load = useCallback(async () => {
        const [owd, owad] = await Promise.all([
            fetchOrdersWithoutDeliveriesFromDb(),
            fetchOrdersWithActiveDeliveriesFromDb(),
        ]);

        setOrdersWithoutDeliveries(
            owd.filter(order => {
                const s = (order.status ?? '').trim().toUpperCase();
                return (order.type === 'ecom' || order.type === 'chat') &&
                    s === ORDER_STATUS.ASSIGN_DRIVER &&
                    !EXCLUDED_DISPATCH_STATUSES.includes(s as typeof ORDER_STATUS[keyof typeof ORDER_STATUS])
            })
        );

        const ecomOrChat = owad.filter(o => o.type === 'ecom' || o.type === 'chat');
        setPendingAcceptance(ecomOrChat.filter(o =>
            (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.DRIVER_ASSIGNED
        ));
        setOrdersWithActiveDeliveries(ecomOrChat.filter(o =>
            (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.ON_ROUTE
        ));
    }, []);

    useEffect(() => { void load(); }, [load]);

    const goToOrder = (id: string | number) => navigate(`/orders/${id}`);

    const openAssignFor = async (order: Order) => {
        const fullOrder = await fetchSingleOrderFromDb(String(order.id)) ?? order;
        setSelectedOrder(fullOrder);
        const initialAddressId = fullOrder.delivery_address_id || fullOrder.customer?.delivery_address || null;
        setNewDelivery({ destination_id: initialAddressId, driver_id: null });
        if (fullOrder.customer_id) {
            const fetched = await fetchAddressesForCustomer(String(fullOrder.customer_id));
            setAddresses(fetched);
        }
        setIsCreateDeliveryModalOpen(true);
    };

    const handleDeliverySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedOrder || !newDelivery.destination_id || !newDelivery.driver_id) return;
        setLoading(true);
        try {
            const success = await assignDriverToOrderInDb(
                String(selectedOrder.id),
                selectedOrder.customer_id,
                newDelivery.destination_id,
                newDelivery.driver_id,
            );
            if (success) {
                setIsCreateDeliveryModalOpen(false);
                setSelectedOrder(null);
                void load();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmUnassign = async () => {
        if (!orderToUnassign) return;
        setLoading(true);
        try {
            await unassignDriverFromOrderInDb(orderToUnassign);
            setIsUnassignModalOpen(false);
            setOrderToUnassign(null);
            void load();
        } catch {
            // error is non-fatal here — table will refresh on next load
        } finally {
            setLoading(false);
        }
    };

    const TH = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500';

    return (
        <div className="min-h-screen space-y-8">
            <header>
                <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Delivery Dispatch</h1>
                <p className="text-sm text-stone-500 mt-1">Manage driver allocation and monitor real-time fulfillment status.</p>
            </header>

            {/* Assign a driver */}
            <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="px-5 py-3 border-b border-stone-200">
                    <h2 className="font-semibold text-stone-900">Assign a Driver to an Order</h2>
                </div>
                {ordersWithoutDeliveries === null ? (
                    <div className="p-4 text-sm text-stone-400">Loading…</div>
                ) : ordersWithoutDeliveries.length === 0 ? (
                    <div className="p-4 text-sm text-stone-500">No pending assignments.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className={TH}>Order Ref</th>
                                <th className={TH}>Customer</th>
                                <th className={TH}>Type</th>
                                <th className={TH}>Status</th>
                                <th className={TH}>Created</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {ordersWithoutDeliveries.map(order => (
                                <tr key={order.id} onClick={() => goToOrder(order.id)}
                                    className="cursor-pointer hover:bg-stone-50 transition">
                                    <td className="px-4 py-3 font-medium text-stone-900">{order.reference}</td>
                                    <td className="px-4 py-3 text-stone-700">
                                        {order.customer?.first_name} {order.customer?.last_name}
                                    </td>
                                    <td className="px-4 py-3">
                                        <OrderTypeBadge type={order.type} />
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={order.status ?? ''} /></td>
                                    <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={e => { e.stopPropagation(); void openAssignFor(order); }}
                                                className="px-3 py-1.5 bg-stone-900 text-white text-xs rounded-full hover:bg-stone-700 transition">
                                            Assign
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Pending acceptance */}
            <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="px-5 py-3 border-b border-stone-200">
                    <h2 className="font-semibold text-stone-900">Pending Driver Acceptance</h2>
                </div>
                {pendingAcceptance === null ? (
                    <div className="p-4 text-sm text-stone-400">Loading…</div>
                ) : pendingAcceptance.length === 0 ? (
                    <div className="p-4 text-sm text-stone-500">No orders waiting for driver acceptance.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className={TH}>Order Ref</th>
                                <th className={TH}>Customer</th>
                                <th className={TH}>Assigned Driver</th>
                                <th className={TH}>Time Elapsed</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {pendingAcceptance.map(order => (
                                <tr key={order.id} onClick={() => goToOrder(order.id)}
                                    className="cursor-pointer hover:bg-stone-50 transition">
                                    <td className="px-4 py-3 font-medium text-stone-900">{order.order_reference}</td>
                                    <td className="px-4 py-3 text-stone-700">{order.customer}</td>
                                    <td className="px-4 py-3">
                                            <span className="border border-stone-300 px-2 py-0.5 text-xs text-stone-700 rounded-full inline-flex items-center gap-1.5">
                                                <span className="h-2 w-2 rounded-full bg-[#f5ab40] animate-pulse" />
                                                {order.driver_name}
                                            </span>
                                    </td>
                                    <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.assigned_date)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={e => { e.stopPropagation(); setOrderToUnassign(order.id); setIsUnassignModalOpen(true); }}
                                                disabled={loading}
                                                className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 transition disabled:opacity-50">
                                            Unassign
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Active deliveries */}
            <ActiveDeliveriesTable orders={ordersWithActiveDeliveries} />

            <ConfirmModal
                isOpen={isUnassignModalOpen}
                title="Unassign Driver"
                message="Are you sure you want to remove this driver from the order?"
                onConfirm={handleConfirmUnassign}
                onCancel={() => { setIsUnassignModalOpen(false); setOrderToUnassign(null); }}
                loading={loading}>
                <div className="flex items-center gap-3 text-stone-600 rounded-lg">
                    <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-xs font-medium">The order will be moved back to the 'Assign Driver' list, and the driver will no longer see it in their app.</p>
                </div>
            </ConfirmModal>

            {isCreateDeliveryModalOpen && (
                <CreateDeliveryModal
                    isOpen={isCreateDeliveryModalOpen}
                    onClose={() => setIsCreateDeliveryModalOpen(false)}
                    newDelivery={newDelivery}
                    onDeliveryChange={e => setNewDelivery(p => ({ ...p, destination_id: e.target.value }))}
                    onDriverChange={e => setNewDelivery(p => ({ ...p, driver_id: e.target.value  }))}
                    onSubmit={handleDeliverySubmit}
                    loading={loading}
                    customerAddresses={addresses}
                    selectedOrder={selectedOrder}
                />
            )}
        </div>
    );
};

export default Dispatch;